import React from "react";
import { useTranslation } from "react-i18next";
import { FlightDirection, FlightRecord } from "./types";

export interface RecordPlateProps {
  record: FlightRecord;
  direction: FlightDirection;
}

const TOO_THIN_THRESHOLD = 10;

const formatDuration = (minutes: number): string => {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

const formatCancelledCount = (t: (key: string, opts?: any) => string, count: number): string => {
  if (count === 0) return t("record.cancelledNever");
  if (count === 1) return t("record.cancelledOnce");
  if (count === 2) return t("record.cancelledTwice");
  return t("record.cancelledMany", { count });
};

const BAND_LABEL_KEY: Record<FlightRecord["bands"][number]["key"], string> = {
  early: "record.bandEarly",
  onTime: "record.bandOnTime",
  late: "record.bandLate",
};

// Figma spec (DASH-6077): early/within-15/late bars reuse the same
// success/primary/pending token trio as the badge dots elsewhere in Flight
// Alerts (see BadgePill.tsx).
const BAND_BAR_CLASS: Record<FlightRecord["bands"][number]["key"], string> = {
  early: "bg-success-100",
  onTime: "bg-primary-100",
  late: "bg-pending-100",
};

export const RecordPlate: React.FC<RecordPlateProps> = ({ record, direction }) => {
  const { t } = useTranslation("flightAlerts");
  const isTooThin = record.sampleSize < TOO_THIN_THRESHOLD;

  return (
    <div className="flex flex-col gap-4 rounded-2xl border-2 border-ink-06 bg-reference p-8">
      {/* Figma spec: Material Symbols Rounded 16px ink/40 icon + Lexend
          Deca Bold 10px/12px, 0.6px letter-spacing, uppercase, ink/40
          (DASH-6077). TODO(DASH-6077): confirm the exact Material Symbols
          glyph name — using "history" as a placeholder. */}
      <span className="inline-flex w-fit items-center gap-2">
        <span className="font-material-symbols text-[16px] leading-none text-ink-40">
          history
        </span>
        <span className="font-lexend text-[10px]/[12px] font-bold uppercase tracking-[0.6px] text-ink-40">
          {t("record.heading", { days: record.windowDays })}
        </span>
      </span>

      {isTooThin ? (
        <p className="font-lexend text-[16px]/[20px] font-normal tracking-[-0.1px] text-ink-60">
          {t("record.tooThin")}
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-1">
            {/* Figma spec: value Lexend Deca Bold 32px/32px -1px ink/100;
                qualifier word Lexend Deca Bold 20px/24px -0.4px, colored by
                early/within-15/late (DASH-6077). TODO(DASH-6077): the
                early/late split below is inferred from medianMinutes sign —
                confirm against Figma whether a near-zero median should
                instead show the "within 15m" wording. */}
            {record.medianMinutes === 0 ? (
              <span className="font-lexend text-[32px]/[32px] font-bold tracking-[-1px] text-primary-100">
                {t("record.medianOnTime")}
              </span>
            ) : (
              <div className="flex items-baseline gap-3">
                <span className="font-lexend text-[32px]/[32px] font-bold tracking-[-1px] text-ink-100">
                  {t("record.medianValue", { minutes: Math.abs(record.medianMinutes) })}
                </span>
                <span
                  className={`font-lexend text-[20px]/[24px] font-bold tracking-[-0.4px] ${
                    record.medianMinutes < 0 ? "text-success-100" : "text-pending-100"
                  }`}
                >
                  {record.medianMinutes < 0
                    ? t("record.medianQualifierEarly")
                    : t("record.medianQualifierLate")}
                </span>
              </div>
            )}
            <p className="font-lexend text-[16px]/[20px] font-normal tracking-[-0.1px] text-ink-60">
              {direction === "arrival"
                ? t("record.subtitleArrival", { count: record.sampleSize })
                : t("record.subtitleDeparture", { count: record.sampleSize })}
            </p>
          </div>

          <div className="h-px w-full bg-ink-10" />

          <div className="flex flex-col gap-3">
            {/* Below 355px, the fixed 150px label leaves the bar almost no
                room (label + count already eat the ~211px content width), so
                it renders as an invisible sliver. Stack label above bar+count
                instead of dropping the bar — it stays paired with its count
                so the two never get separated onto different lines. */}
            {record.bands.map((band) => (
              <div
                key={band.key}
                className="flex w-full flex-col gap-1 min-[356px]:flex-row min-[356px]:items-center min-[356px]:gap-4"
              >
                <span className="font-lexend text-[14px]/[18px] font-normal text-ink-100 min-[356px]:w-[150px] min-[356px]:shrink-0">
                  {t(BAND_LABEL_KEY[band.key])}
                </span>
                <div className="flex flex-1 items-center gap-4">
                  <div className="h-3 flex-1 overflow-hidden rounded-full bg-ink-10">
                    <div
                      className={`h-full rounded-full ${BAND_BAR_CLASS[band.key]}`}
                      style={{ width: `${(band.count / record.sampleSize) * 100}%` }}
                    />
                  </div>
                  <span className="w-4 shrink-0 text-right font-lexend text-[14px]/[18px] font-normal text-ink-100">
                    {band.count}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="h-px w-full bg-ink-10" />

          <div className="flex flex-wrap gap-x-10 gap-y-3">
            <div className="flex flex-col gap-1">
              <span className="font-lexend text-[12px]/[16px] font-normal text-ink-60">
                {direction === "arrival"
                  ? t("record.earlyOrOnTimeLabel")
                  : t("record.leftOnTimeLabel")}
              </span>
              <span className="font-lexend text-[20px]/[24px] font-bold tracking-[-0.4px] text-ink-100">
                {record.earlyOrOnTimePct}%
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="font-lexend text-[12px]/[16px] font-normal text-ink-60">
                {t("record.worstDelay")}
              </span>
              <span className="font-lexend text-[20px]/[24px] font-bold tracking-[-0.4px] text-ink-100">
                {formatDuration(record.worstDelayMinutes)} · {record.worstDelayDate}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="font-lexend text-[12px]/[16px] font-normal text-ink-60">
                {t("record.cancelled")}
              </span>
              <span className="font-lexend text-[20px]/[24px] font-bold tracking-[-0.4px] text-ink-100">
                {formatCancelledCount(t, record.cancelledCount)}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
